import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { apiClient } from './client';
import type {
  AdminNotification,
  AuditLog,
  Capability,
  CapabilityProviderMap,
  Category,
  Consumer,
  DashboardStats,
  JobDetail,
  JobListItem,
  JobStatus,
  MediaUploadResult,
  Me,
  ModelGroup,
  ModelGroupMember,
  ModelTestResult,
  Paginated,
  Provider,
  ProviderErrorRule,
  ProviderModel,
  ProviderSubSource,
  QueueStats,
  SubSourceKind,
  SettingsMap,
  Webhook,
  WebhookDelivery,
  Workflow,
  WorkflowChildJob,
  Worker,
  WorkerHistoryItem,
  WorkerInstallInfo,
  WorkerLatestRelease,
  WorkerLiveState,
} from './types';

// ---------- Khoá cache ----------
export const qk = {
  me: ['me'] as const,
  capabilities: ['capabilities'] as const,
  capability: (id: string) => ['capabilities', id] as const,
  capabilityProviders: (id: string) => ['capabilities', id, 'providers'] as const,
  providers: ['providers'] as const,
  provider: (id: string) => ['providers', id] as const,
  errorRules: (id: string) => ['providers', id, 'error-rules'] as const,
  providerModels: (id: string) => ['providers', id, 'models'] as const,
  subSources: (id: string) => ['providers', id, 'subsources'] as const,
  allSubSources: ['subsources'] as const,
  allModels: ['models'] as const,
  modelTest: (testId: string) => ['model-tests', testId] as const,
  modelGroups: ['model-groups'] as const,
  modelGroup: (id: string) => ['model-groups', id] as const,
  workflows: ['workflows'] as const,
  workflow: (id: string) => ['workflows', id] as const,
  workflowChildren: (id: string) => ['jobs', id, 'workflow-children'] as const,
  groupMembers: (id: string) => ['model-groups', id, 'members'] as const,
  workers: ['workers'] as const,
  workerHistory: (id: string) => ['workers', id, 'jobs'] as const,
  workerInstallInfo: ['worker-install-info'] as const,
  workerLatestRelease: ['worker-latest-release'] as const,
  queueStats: ['queue', 'stats'] as const,
  jobs: (status: string, type: string, consumerId: string, page: number, pageSize: number, workflowSteps: string) =>
    ['jobs', status, type, consumerId, page, pageSize, workflowSteps] as const,
  job: (id: string) => ['jobs', id] as const,
  raceChildren: (id: string) => ['jobs', id, 'race-children'] as const,
  webhooks: ['webhooks'] as const,
  deliveries: (status: string) => ['webhook-deliveries', status] as const,
  consumers: ['consumers'] as const,
  auditLogs: (page: number, pageSize: number) => ['audit-logs', page, pageSize] as const,
  settings: ['settings'] as const,
  categories: ['categories'] as const,
  notifications: ['notifications'] as const,
};

// ---------- Auth ----------
export function useMe(options?: Partial<UseQueryOptions<Me>>) {
  return useQuery<Me>({
    queryKey: qk.me,
    queryFn: async () => (await apiClient.get<Me>('/me')).data,
    retry: false,
    staleTime: 30_000,
    ...options,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { username: string; password: string }) =>
      (await apiClient.post<Me>('/login', body)).data,
    onSuccess: (data) => {
      qc.setQueryData(qk.me, data);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await apiClient.post('/logout')).data,
    onSuccess: () => {
      qc.clear();
    },
  });
}

// ---------- Capabilities ----------
export function useCapabilities() {
  return useQuery<Capability[]>({
    queryKey: qk.capabilities,
    queryFn: async () => (await apiClient.get<Capability[]>('/capabilities')).data,
  });
}

export function useCapability(id?: string) {
  return useQuery<Capability>({
    queryKey: qk.capability(id ?? ''),
    enabled: !!id,
    queryFn: async () => (await apiClient.get<Capability>(`/capabilities/${id}`)).data,
  });
}

export function useSaveCapability() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id?: string; data: Partial<Capability> }) => {
      if (id) return (await apiClient.put(`/capabilities/${id}`, data)).data;
      return (await apiClient.post('/capabilities', data)).data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.capabilities }),
  });
}

export function useDeleteCapability() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await apiClient.delete(`/capabilities/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.capabilities }),
  });
}

export function useCapabilityProviders(id?: string) {
  return useQuery<CapabilityProviderMap[]>({
    queryKey: qk.capabilityProviders(id ?? ''),
    enabled: !!id,
    queryFn: async () =>
      (await apiClient.get<CapabilityProviderMap[]>(`/capabilities/${id}/providers`)).data,
  });
}

export function useAddCapabilityProvider(capabilityId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { providerId: string; priority: number }) =>
      (await apiClient.post(`/capabilities/${capabilityId}/providers`, body)).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: qk.capabilityProviders(capabilityId) }),
  });
}

export function useUpdateCapabilityProvider(capabilityId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      mapId,
      body,
    }: {
      mapId: string;
      body: { priority?: number; enabled?: boolean };
    }) =>
      (await apiClient.put(`/capabilities/${capabilityId}/providers/${mapId}`, body)).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: qk.capabilityProviders(capabilityId) }),
  });
}

export function useRemoveCapabilityProvider(capabilityId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (mapId: string) =>
      (await apiClient.delete(`/capabilities/${capabilityId}/providers/${mapId}`)).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: qk.capabilityProviders(capabilityId) }),
  });
}

// ---------- Providers ----------
export function useProviders() {
  return useQuery<Provider[]>({
    queryKey: qk.providers,
    queryFn: async () => (await apiClient.get<Provider[]>('/providers')).data,
  });
}

export function useProvider(id?: string) {
  return useQuery<Provider>({
    queryKey: qk.provider(id ?? ''),
    enabled: !!id,
    queryFn: async () => (await apiClient.get<Provider>(`/providers/${id}`)).data,
  });
}

export function useSaveProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id?: string; data: Partial<Provider> }) => {
      if (id) return (await apiClient.put(`/providers/${id}`, data)).data;
      return (await apiClient.post('/providers', data)).data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.providers }),
  });
}

export function useDeleteProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await apiClient.delete(`/providers/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.providers }),
  });
}

/** Đăng ký (hoặc đăng ký lại) webhook với nguồn Plenxai Free ngay lập tức. */
export function useRegisterWebhook(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await apiClient.post<{ webhookId: string; callbackUrl: string }>(`/providers/${id}/register-webhook`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.provider(id) }),
  });
}

export function useErrorRules(providerId?: string) {
  return useQuery<ProviderErrorRule[]>({
    queryKey: qk.errorRules(providerId ?? ''),
    enabled: !!providerId,
    queryFn: async () =>
      (await apiClient.get<ProviderErrorRule[]>(`/providers/${providerId}/error-rules`))
        .data,
  });
}

export function useSaveErrorRule(providerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      ruleId,
      data,
    }: {
      ruleId?: string;
      data: Partial<ProviderErrorRule>;
    }) => {
      if (ruleId)
        return (
          await apiClient.put(`/providers/${providerId}/error-rules/${ruleId}`, data)
        ).data;
      return (await apiClient.post(`/providers/${providerId}/error-rules`, data)).data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.errorRules(providerId) }),
  });
}

export function useDeleteErrorRule(providerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ruleId: string) =>
      (await apiClient.delete(`/providers/${providerId}/error-rules/${ruleId}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.errorRules(providerId) }),
  });
}

// ---------- Provider models ----------
export function useProviderModels(providerId?: string) {
  return useQuery<ProviderModel[]>({
    queryKey: qk.providerModels(providerId ?? ''),
    enabled: !!providerId,
    queryFn: async () =>
      (await apiClient.get<ProviderModel[]>(`/providers/${providerId}/models`)).data,
  });
}

export function useSaveProviderModel(providerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ modelId, data }: { modelId?: string; data: Partial<ProviderModel> }) => {
      if (modelId)
        return (await apiClient.put(`/providers/${providerId}/models/${modelId}`, data)).data;
      return (await apiClient.post(`/providers/${providerId}/models`, data)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.providerModels(providerId) });
      qc.invalidateQueries({ queryKey: qk.allModels });
    },
  });
}

/** Videogen: gọi GET /api/v1/models của nguồn → upsert toàn bộ model + paramsSchema động. */
export function useImportVideogenModels(providerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (
        await apiClient.post<{
          imported: number;
          created: number;
          updated: number;
          codes: string[];
          models: Array<{ code: string; params: string[] }>;
        }>(`/providers/${providerId}/videogen/import-models`)
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.providerModels(providerId) });
      qc.invalidateQueries({ queryKey: qk.allModels });
    },
  });
}

export function useDeleteProviderModel(providerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (modelId: string) =>
      (await apiClient.delete(`/providers/${providerId}/models/${modelId}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.providerModels(providerId) });
      qc.invalidateQueries({ queryKey: qk.allModels });
    },
  });
}

/** Tất cả model (mọi provider) — để chọn thành viên cho nhóm model. */
export function useAllModels() {
  return useQuery<ProviderModel[]>({
    queryKey: qk.allModels,
    queryFn: async () => (await apiClient.get<ProviderModel[]>('/models')).data,
  });
}

// ---------- Nguồn con (sub-source, vd Vilao) ----------
export function useSubSources(providerId?: string) {
  return useQuery<ProviderSubSource[]>({
    queryKey: qk.subSources(providerId ?? ''),
    enabled: !!providerId,
    queryFn: async () =>
      (await apiClient.get<ProviderSubSource[]>(`/providers/${providerId}/subsources`)).data,
  });
}

export function useSaveSubSource(providerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ subId, data }: { subId?: string; data: Partial<ProviderSubSource> }) => {
      if (subId) return (await apiClient.put(`/providers/${providerId}/subsources/${subId}`, data)).data;
      return (await apiClient.post(`/providers/${providerId}/subsources`, data)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.subSources(providerId) });
      qc.invalidateQueries({ queryKey: qk.allSubSources });
    },
  });
}

export function useDeleteSubSource(providerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (subId: string) =>
      (await apiClient.delete(`/providers/${providerId}/subsources/${subId}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.subSources(providerId) });
      qc.invalidateQueries({ queryKey: qk.allSubSources });
    },
  });
}

/** Tất cả nguồn con (mọi provider) — để chọn thành viên cho nhóm model. */
export function useAllSubSources() {
  return useQuery<ProviderSubSource[]>({
    queryKey: qk.allSubSources,
    queryFn: async () => (await apiClient.get<ProviderSubSource[]>('/subsources')).data,
  });
}

/** Kiểm thử 1 nguồn con (Vilao) với thao tác op = chat|image (ảnh tạo & sửa dùng chung). */
export function useStartSubSourceTest(providerId: string) {
  return useMutation({
    mutationFn: async ({
      subId,
      op,
      input,
    }: {
      subId: string;
      op: SubSourceKind;
      input: unknown;
    }) =>
      (
        await apiClient.post<{ testId: string }>(
          `/providers/${providerId}/subsources/${subId}/test`,
          { op, input },
        )
      ).data,
  });
}

// ---------- Kiểm thử model + upload media ----------
export function useStartModelTest(providerId: string) {
  return useMutation({
    mutationFn: async ({ modelId, input }: { modelId: string; input: unknown }) =>
      (await apiClient.post<{ testId: string }>(`/providers/${providerId}/models/${modelId}/test`, { input }))
        .data,
  });
}

/** Kiểm thử 1 NHÓM MODEL (xoay vòng member như vận hành thật). */
export function useStartGroupTest(groupId: string) {
  return useMutation({
    mutationFn: async ({ input }: { input: unknown }) =>
      (await apiClient.post<{ testId: string }>(`/model-groups/${groupId}/test`, { input })).data,
  });
}

/** Poll kết quả kiểm thử; dừng poll khi không còn 'running'. */
export function useModelTest(testId?: string) {
  return useQuery<ModelTestResult>({
    queryKey: qk.modelTest(testId ?? ''),
    enabled: !!testId,
    refetchInterval: (q) => (q.state.data?.status === 'running' ? 2500 : false),
    queryFn: async () => (await apiClient.get<ModelTestResult>(`/model-tests/${testId}`)).data,
  });
}

export function useUploadMedia(providerId: string) {
  return useMutation({
    // Gửi file NHỊ PHÂN (multipart) — không base64, không treo trình duyệt.
    // Dùng fetch để trình duyệt tự đặt Content-Type multipart kèm boundary
    // (apiClient mặc định application/json sẽ làm hỏng multipart).
    mutationFn: async (fd: FormData): Promise<MediaUploadResult> => {
      const res = await fetch(`/admin/api/providers/${providerId}/media-upload`, {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
      });
      if (!res.ok) {
        let msg = `Upload thất bại (HTTP ${res.status})`;
        try {
          const j = await res.json();
          if (j?.message) msg = Array.isArray(j.message) ? j.message.join(', ') : j.message;
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }
      return res.json();
    },
  });
}

export interface ZapcapSound {
  slug: string;
  label: string;
  url: string;
}

/** Zapcap: lấy danh sách theme + sound từ nguồn, lưu vĩnh viễn vào provider.config. */
export function useRefreshZapcapCatalog(providerId: string) {
  return useMutation({
    mutationFn: async () =>
      (await apiClient.post<{ themes: string[]; sounds: string[]; soundDetails: ZapcapSound[] }>(
        `/providers/${providerId}/zapcap/refresh-catalog`,
      )).data,
  });
}

export interface WatermarkTemplate {
  id: number | string;
  name: string;
  description?: string;
  method?: string;
  sensitivity?: string;
  shape?: string;
  labels?: Array<{ name: string; value: string }>;
}

/** Watermark: lấy danh sách template từ nguồn, lưu vào provider.config (dropdown "Template"). */
export function useRefreshWatermarkTemplates(providerId: string) {
  return useMutation({
    mutationFn: async () =>
      (await apiClient.post<{ templates: WatermarkTemplate[] }>(
        `/providers/${providerId}/watermark/refresh-templates`,
      )).data,
  });
}

export interface ZapcapWidget {
  text: string;
  emoji: string;
  important: boolean;
  start_time?: number;
  end_time?: number;
}
export interface ZapcapChunk {
  index: number;
  emoji: string;
  sound: string;
  sound_volume: string;
  start_time?: number;
  end_time?: number;
  widgets: ZapcapWidget[];
}
export interface ZapcapProject {
  project_id: string;
  options: Record<string, unknown>;
  chunks: ZapcapChunk[];
}

/** Zapcap: nạp thông số gốc + toàn bộ caption (chunks/widgets) của 1 project đã có. */
export function useLoadZapcapProject(providerId: string) {
  return useMutation({
    mutationFn: async (projectId: string) =>
      (await apiClient.get<ZapcapProject>(
        `/providers/${providerId}/zapcap/project/${encodeURIComponent(projectId)}`,
        { timeout: 130000 },
      )).data,
  });
}

// ---------- Model groups (Nhóm model) ----------
export function useModelGroups() {
  return useQuery<ModelGroup[]>({
    queryKey: qk.modelGroups,
    queryFn: async () => (await apiClient.get<ModelGroup[]>('/model-groups')).data,
  });
}

export function useSaveModelGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id?: string; data: Partial<ModelGroup> }) => {
      if (id) return (await apiClient.put(`/model-groups/${id}`, data)).data;
      return (await apiClient.post('/model-groups', data)).data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.modelGroups }),
  });
}

export function useDeleteModelGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await apiClient.delete(`/model-groups/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.modelGroups }),
  });
}

export function useGroupMembers(groupId?: string) {
  return useQuery<ModelGroupMember[]>({
    queryKey: qk.groupMembers(groupId ?? ''),
    enabled: !!groupId,
    queryFn: async () =>
      (await apiClient.get<ModelGroupMember[]>(`/model-groups/${groupId}/members`)).data,
  });
}

export function useAddGroupMember(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      modelId?: string;
      subSourceId?: string;
      modelKind?: SubSourceKind;
      priority: number;
    }) => (await apiClient.post(`/model-groups/${groupId}/members`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.groupMembers(groupId) }),
  });
}

export function useUpdateGroupMember(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      memberId,
      body,
    }: {
      memberId: string;
      body: {
        priority?: number;
        enabled?: boolean;
        paramMap?: import('./types').ParamMap | null;
        overrideConcurrent?: number | null;
        overrideRateLimit?: number | null;
        useProviderRetryCount?: boolean;
        retryCountOverride?: number | null;
        onlyOnError?: boolean;
        /** Cho phép member tham gia chế độ "Tạo job tất cả model" (race). */
        allowRaceAll?: boolean;
      };
    }) => (await apiClient.put(`/model-groups/${groupId}/members/${memberId}`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.groupMembers(groupId) }),
  });
}

export function useRemoveGroupMember(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (memberId: string) =>
      (await apiClient.delete(`/model-groups/${groupId}/members/${memberId}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.groupMembers(groupId) });
      qc.invalidateQueries({ queryKey: qk.modelGroups });
    },
  });
}

/** Đặt (hoặc bỏ) model mặc định của nhóm — model này được thử trước tiên. */
export function useSetGroupDefaultModel(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (modelId: string | null) =>
      (await apiClient.put(`/model-groups/${groupId}/default-model`, { modelId })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.modelGroups });
      qc.invalidateQueries({ queryKey: qk.modelGroup(groupId) });
    },
  });
}

/** Reset tạm khóa (circuit breaker) cho TẤT CẢ model con trong nhóm — mở khóa thủ công. */
export function useResetGroupCooldown() {
  return useMutation({
    mutationFn: async (groupId: string) =>
      (await apiClient.post(`/model-groups/${groupId}/reset-cooldown`)).data as { ok: boolean; resetCount: number },
  });
}

// ---------- Workflow model (chuỗi bước) ----------
export function useWorkflows() {
  return useQuery<Workflow[]>({
    queryKey: qk.workflows,
    queryFn: async () => (await apiClient.get<Workflow[]>('/workflows')).data,
  });
}

export function useWorkflow(id?: string) {
  return useQuery<Workflow>({
    queryKey: qk.workflow(id ?? ''),
    enabled: !!id,
    queryFn: async () => (await apiClient.get<Workflow>(`/workflows/${id}`)).data,
  });
}

export function useSaveWorkflow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id?: string; data: Partial<Workflow> }) => {
      if (id) return (await apiClient.put(`/workflows/${id}`, data)).data as Workflow;
      return (await apiClient.post('/workflows', data)).data as Workflow;
    },
    onSuccess: (w: Workflow) => {
      qc.invalidateQueries({ queryKey: qk.workflows });
      if (w?.id) qc.invalidateQueries({ queryKey: qk.workflow(w.id) });
    },
  });
}

export function useDeleteWorkflow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await apiClient.delete(`/workflows/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.workflows }),
  });
}

export function useDuplicateWorkflow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await apiClient.post(`/workflows/${id}/duplicate`)).data as Workflow,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.workflows }),
  });
}

/** Chạy thử workflow — trả testId, poll bằng useModelTest như model/nhóm model. */
export function useStartWorkflowTest(workflowId?: string) {
  return useMutation({
    mutationFn: async (body: { input: unknown }) =>
      (await apiClient.post<{ testId: string }>(`/workflows/${workflowId}/test`, body)).data,
  });
}

/** Job con từng bước của một job cha workflow (trang Lịch sử). */
export function useWorkflowChildren(jobId?: string, enabled = true) {
  return useQuery<WorkflowChildJob[]>({
    queryKey: qk.workflowChildren(jobId ?? ''),
    enabled: !!jobId && enabled,
    queryFn: async () => (await apiClient.get<WorkflowChildJob[]>(`/jobs/${jobId}/workflow-children`)).data,
  });
}

// ---------- Workers ----------
export function useWorkers() {
  return useQuery<Worker[]>({
    queryKey: qk.workers,
    queryFn: async () => (await apiClient.get<Worker[]>('/workers')).data,
    refetchInterval: 5_000,
  });
}

/** Trạng thái realtime của worker đang kết nối (poll nhanh khi mở modal). */
export function useWorkersLive(enabled = true, refetchInterval = 1500) {
  return useQuery<WorkerLiveState[]>({
    queryKey: ['workers', 'live'],
    enabled,
    refetchInterval: enabled ? refetchInterval : false,
    queryFn: async () => (await apiClient.get<WorkerLiveState[]>('/workers/live')).data,
  });
}

export function useCreateWorker() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { name: string; concurrency?: number; note?: string }) =>
      (await apiClient.post<Worker>('/workers', body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.workers }),
  });
}

export function useUpdateWorker() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: { enabled?: boolean; note?: string; concurrency?: number };
    }) => (await apiClient.patch<Worker>(`/workers/${id}`, body)).data,
    // Cập nhật lạc quan (optimistic) cho danh sách worker.
    onMutate: async ({ id, body }) => {
      await qc.cancelQueries({ queryKey: qk.workers });
      const prev = qc.getQueryData<Worker[]>(qk.workers);
      if (prev) {
        qc.setQueryData<Worker[]>(
          qk.workers,
          prev.map((w) => (w.id === id ? { ...w, ...body } : w)),
        );
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(qk.workers, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: qk.workers }),
  });
}

export function useDeleteWorker() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await apiClient.delete(`/workers/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.workers }),
  });
}

/** Yêu cầu tất cả worker đang kết nối tự cập nhật phần mềm lên bản mới nhất. */
export function useUpdateAllWorkers() {
  return useMutation({
    mutationFn: async () =>
      (await apiClient.post<{ sent: number; total: number }>('/workers/update-all')).data,
  });
}

export function useWorkerHistory(id?: string, enabled = true) {
  return useQuery<WorkerHistoryItem[]>({
    queryKey: qk.workerHistory(id ?? ''),
    enabled: !!id && enabled,
    queryFn: async () =>
      (
        await apiClient.get<WorkerHistoryItem[]>(`/workers/${id}/jobs`, {
          params: { limit: 50 },
        })
      ).data,
  });
}

export function useWorkerInstallInfo(enabled = true) {
  return useQuery<WorkerInstallInfo>({
    queryKey: qk.workerInstallInfo,
    enabled,
    staleTime: 60_000,
    queryFn: async () =>
      (await apiClient.get<WorkerInstallInfo>('/worker-install-info')).data,
  });
}

// latest.json nằm ngoài baseURL '/admin/api' (cùng origin, công khai).
// Dùng fetch thuần và bỏ qua lỗi 404 (trả về null).
export function useWorkerLatestRelease(enabled = true) {
  return useQuery<WorkerLatestRelease | null>({
    queryKey: qk.workerLatestRelease,
    enabled,
    staleTime: 60_000,
    retry: false,
    queryFn: async () => {
      const res = await fetch('/worker/download/latest.json', {
        credentials: 'same-origin',
      });
      if (!res.ok) return null;
      return (await res.json()) as WorkerLatestRelease;
    },
  });
}

// ---------- Queue ----------
export function useQueueStats(refetchInterval = 5_000) {
  return useQuery<QueueStats>({
    queryKey: qk.queueStats,
    queryFn: async () => (await apiClient.get<QueueStats>('/queue/stats')).data,
    refetchInterval,
  });
}

// ---------- Thống kê (có bộ lọc thời gian) ----------
export interface StatsRange {
  range?: string;
  since?: string;
  until?: string;
}

export function useDashboardStats(params: StatsRange, refetchInterval = 30_000) {
  return useQuery<DashboardStats>({
    queryKey: ['stats', params],
    queryFn: async () => (await apiClient.get<DashboardStats>('/stats', { params })).data,
    refetchInterval,
  });
}

// ---------- Jobs ----------
// Trạng thái đã kết thúc (không cần poll thêm). Mọi trạng thái khác = đang chạy -> tự làm mới.
const TERMINAL_JOB_STATUS: string[] = ['completed', 'failed', 'cancelled'];

export function useJobs(
  status: string,
  type: string,
  consumerId: string,
  page: number,
  pageSize: number,
  /** Buoc workflow (job con): 'hide' (mac dinh) | 'show' (hien cung job cha) | 'only'. */
  workflowSteps: string = 'hide',
) {
  return useQuery<Paginated<JobListItem>>({
    queryKey: qk.jobs(status, type, consumerId, page, pageSize, workflowSteps),
    queryFn: async () => {
      const params: Record<string, string | number> = { page, pageSize };
      if (status) params.status = status;
      if (type) params.type = type;
      if (consumerId) params.consumerId = consumerId;
      if (workflowSteps && workflowSteps !== 'hide') params.workflowSteps = workflowSteps;
      return (await apiClient.get<Paginated<JobListItem>>('/jobs', { params })).data;
    },
    // Tự làm mới khi còn job ĐANG XỬ LÝ (webhook về / failover xong) -> output hiện ngay, không cần F5.
    refetchInterval: (q) =>
      (q.state.data?.items ?? []).some((j) => !TERMINAL_JOB_STATUS.includes(j.status)) ? 5_000 : false,
  });
}

export function useJob(id?: string) {
  return useQuery<JobDetail>({
    queryKey: qk.job(id ?? ''),
    enabled: !!id,
    // Chi tiết đang mở của job CHƯA xong -> tự cập nhật (kết quả/lần thử/model con) tới khi hoàn tất.
    refetchInterval: (q) => (q.state.data && !TERMINAL_JOB_STATUS.includes(q.state.data.status) ? 4_000 : false),
    queryFn: async () => (await apiClient.get<JobDetail>(`/jobs/${id}`)).data,
  });
}

export function useCancelJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await apiClient.post(`/jobs/${id}/cancel`)).data,
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ['jobs'] });
      qc.invalidateQueries({ queryKey: qk.job(id) });
    },
  });
}

export function useCancelAllJobs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await apiClient.post<{ ok: boolean; count: number }>('/jobs/cancel-all')).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jobs'] }),
  });
}

export function useCancelWaitingJobs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await apiClient.post<{ ok: boolean; count: number }>('/jobs/cancel-waiting')).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jobs'] }),
  });
}

export function useDeleteJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await apiClient.delete(`/jobs/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jobs'] }),
  });
}

export function useDeleteJobs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) =>
      (await apiClient.post<{ ok: boolean; count: number }>('/jobs/delete-many', { ids })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jobs'] }),
  });
}

// ---------- Race children ----------
export function useRaceChildren(parentId?: string) {
  return useQuery<import('./types').RaceChild[]>({
    queryKey: qk.raceChildren(parentId ?? ''),
    enabled: !!parentId,
    queryFn: async () =>
      (await apiClient.get<import('./types').RaceChild[]>(`/jobs/${parentId}/race-children`)).data,
    refetchInterval: parentId ? 3000 : false, // poll every 3s while race is active
  });
}

// ---------- Thông báo admin (Tổng quan) ----------
export function useAdminNotifications(unreadOnly = false) {
  return useQuery<{ items: AdminNotification[]; unreadCount: number }>({
    queryKey: [...qk.notifications, unreadOnly] as const,
    queryFn: async () =>
      (await apiClient.get<{ items: AdminNotification[]; unreadCount: number }>('/notifications', {
        params: unreadOnly ? { unread: '1' } : {},
      })).data,
    // Tự làm mới định kỳ để admin thấy cảnh báo model con lỗi kịp thời.
    refetchInterval: 30_000,
  });
}

export function useMarkNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids?: string[]) =>
      (await apiClient.post<{ ok: boolean; count: number }>('/notifications/read', ids ? { ids } : {})).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.notifications }),
  });
}

// ---------- Webhooks ----------
export function useWebhooks() {
  return useQuery<Webhook[]>({
    queryKey: qk.webhooks,
    queryFn: async () => (await apiClient.get<Webhook[]>('/webhooks')).data,
  });
}

export function useSaveWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id?: string; data: Partial<Webhook> }) => {
      if (id) return (await apiClient.put(`/webhooks/${id}`, data)).data;
      return (await apiClient.post('/webhooks', data)).data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.webhooks }),
  });
}

export function useDeleteWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await apiClient.delete(`/webhooks/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.webhooks }),
  });
}

export function useWebhookDeliveries(status: string) {
  return useQuery<WebhookDelivery[]>({
    queryKey: qk.deliveries(status),
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (status) params.status = status;
      return (await apiClient.get<WebhookDelivery[]>('/webhook-deliveries', { params }))
        .data;
    },
    refetchInterval: 10_000,
  });
}

// ---------- Consumers ----------
export function useConsumers() {
  return useQuery<Consumer[]>({
    queryKey: qk.consumers,
    queryFn: async () => (await apiClient.get<Consumer[]>('/consumers')).data,
  });
}

export function useCreateConsumer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { name: string }) =>
      (await apiClient.post<Consumer>('/consumers', body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.consumers }),
  });
}

export function useToggleConsumer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) =>
      (await apiClient.patch(`/consumers/${id}`, { enabled })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.consumers }),
  });
}

export function useDeleteConsumer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await apiClient.delete(`/consumers/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.consumers }),
  });
}

// ---------- Audit logs ----------
export function useAuditLogs(page: number, pageSize: number) {
  return useQuery<Paginated<AuditLog>>({
    queryKey: qk.auditLogs(page, pageSize),
    queryFn: async () =>
      (await apiClient.get<Paginated<AuditLog>>('/audit-logs', { params: { page, pageSize } }))
        .data,
  });
}

// ---------- Categories (danh mục dùng chung) ----------
export function useCategories() {
  return useQuery<Category[]>({
    queryKey: qk.categories,
    queryFn: async () => (await apiClient.get<Category[]>('/categories')).data,
    staleTime: 30_000,
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { name: string; description?: string; sort?: number }) =>
      (await apiClient.post<Category>('/categories', body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.categories }),
  });
}

export function useUpdateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Category> }) =>
      (await apiClient.put<Category>(`/categories/${id}`, data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.categories }),
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await apiClient.delete(`/categories/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.categories }),
  });
}

// ---------- Settings ----------
export function useSettings() {
  return useQuery<SettingsMap>({
    queryKey: qk.settings,
    queryFn: async () => (await apiClient.get<SettingsMap>('/settings')).data,
  });
}

export function useSaveSetting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { key: string; value: unknown }) =>
      (await apiClient.put('/settings', body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.settings }),
  });
}
